<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  listAdmins,
  createAdmin,
  setAdminStatus,
  removeAdmin,
  type AdminRow,
} from '../api';

const items = ref<AdminRow[]>([]);
const loading = ref(false);
const showCreate = ref(false);
const form = reactive({
  username: '',
  password: '',
  role: 'REVIEWER' as 'REVIEWER' | 'PLATFORM_ADMIN',
  displayName: '',
});

async function reload() {
  loading.value = true;
  try {
    items.value = await listAdmins();
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    loading.value = false;
  }
}

async function onCreateSubmit() {
  if (!form.username || form.username.length < 3) {
    ElMessage.warning('用户名至少 3 位');
    return;
  }
  if (!form.password || form.password.length < 8) {
    ElMessage.warning('密码至少 8 位');
    return;
  }
  try {
    await createAdmin({
      username: form.username,
      password: form.password,
      role: form.role,
      displayName: form.displayName || undefined,
    });
    ElMessage.success('创建成功');
    showCreate.value = false;
    form.username = '';
    form.password = '';
    form.displayName = '';
    reload();
  } catch (e) {
    ElMessage.error(normalize(e));
  }
}

async function onToggleStatus(row: AdminRow) {
  const next = row.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
  try {
    await ElMessageBox.confirm(
      `确认${next === 'DISABLED' ? '停用' : '启用'} ${row.username}?`,
      next === 'DISABLED' ? '停用' : '启用',
      { type: 'warning' },
    );
    await setAdminStatus(row.id, next);
    ElMessage.success('已更新');
    reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel') return;
    ElMessage.error(normalize(e));
  }
}

async function onRemove(row: AdminRow) {
  try {
    await ElMessageBox.confirm(
      `确认移除 ${row.username}?移除后此账号无法登录,历史审核记录保留`,
      '移除账号',
      { type: 'warning' },
    );
    await removeAdmin(row.id);
    ElMessage.success('已移除');
    reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel') return;
    ElMessage.error(normalize(e));
  }
}

function normalize(e: unknown) {
  const err = e as { message?: string; code?: string };
  return err?.message || err?.code || '未知错误';
}

function formatTime(s: string | null): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN');
}

onMounted(reload);
</script>

<template>
  <div class="admins-pane">
    <div class="header">
      <el-button type="primary" @click="showCreate = true">新增审核员 / 管理员</el-button>
      <el-button @click="reload" style="margin-left: 8px">刷新</el-button>
    </div>

    <el-table :data="items" v-loading="loading" stripe>
      <el-table-column prop="username" label="用户名" width="140" />
      <el-table-column prop="displayName" label="显示名" width="140">
        <template #default="{ row }">{{ row.displayName || '-' }}</template>
      </el-table-column>
      <el-table-column label="角色" width="140">
        <template #default="{ row }">
          <el-tag :type="row.role === 'PLATFORM_ADMIN' ? 'danger' : ''" size="small">
            {{ row.role === 'PLATFORM_ADMIN' ? '总后台管理员' : '审核员' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="row.status === 'ACTIVE' ? 'success' : 'info'" size="small">
            {{ row.status === 'ACTIVE' ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="最近登录" width="160">
        <template #default="{ row }">{{ formatTime(row.lastLoginAt) }}</template>
      </el-table-column>
      <el-table-column label="创建" width="160">
        <template #default="{ row }">{{ formatTime(row.createdAt) }}</template>
      </el-table-column>
      <el-table-column label="操作" fixed="right" width="220">
        <template #default="{ row }">
          <el-button size="small" @click="onToggleStatus(row)">
            {{ row.status === 'ACTIVE' ? '停用' : '启用' }}
          </el-button>
          <el-button size="small" type="danger" @click="onRemove(row)">移除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog v-model="showCreate" title="新增账号" width="440px">
      <el-form label-width="80px" @submit.prevent="onCreateSubmit">
        <el-form-item label="用户名" required>
          <el-input v-model="form.username" placeholder="至少 3 位,英文数字下划线" />
        </el-form-item>
        <el-form-item label="密码" required>
          <el-input v-model="form.password" type="password" show-password placeholder="至少 8 位" />
        </el-form-item>
        <el-form-item label="角色" required>
          <el-select v-model="form.role" style="width: 100%">
            <el-option label="审核员(REVIEWER)" value="REVIEWER" />
            <el-option label="总后台管理员(PLATFORM_ADMIN)" value="PLATFORM_ADMIN" />
          </el-select>
        </el-form-item>
        <el-form-item label="显示名">
          <el-input v-model="form.displayName" placeholder="可选,便于辨识" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showCreate = false">取消</el-button>
        <el-button type="primary" @click="onCreateSubmit">创建</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped>
.admins-pane { padding: 4px 0; }
.header { margin-bottom: 16px; }
</style>
